import { reduce } from 'lodash';

import { ITINERARY_SOURCE } from './itinerary';
import { isDigitripsStay } from './segment';
import { SEGMENT_TYPE } from './segmentTypes';

export const africaCountryCodes = ['BW', 'NA', 'ZM', 'ZW', 'ZA', 'RW', 'MZ', 'KE', 'MU', 'TZ', 'UG', 'LS', 'SC', 'SZ', 'MW', 'CG', 'MG'];

/**
 * @param {string} countryCode
 * @returns {boolean}
 */
export const isRestOfWorld = countryCode => !africaCountryCodes.includes(countryCode);

// DigiTrips suppliers whose contracted rates are passed through without the dynamic-rate buffer.
const DIGITRIPS_STATIC_SUPPLIER_CODES = ['ISX', 'SLH', 'HRC'];

/**
 * @param {string} supplierCode
 * @returns {boolean}
 */
export const isDynamicRate = supplierCode => !DIGITRIPS_STATIC_SUPPLIER_CODES.includes(supplierCode);

// itrvl's lifecycle of a DigiTrips booking. `requested` is written before the vendor call, and
// `unknown` means the vendor's answer was lost and its lookups found nothing yet.
// Frozen so the generated declaration carries the string literals rather than
// widening every member to string.
export const DIGITRIPS_BOOKING_STATE = Object.freeze({
  REQUESTED: 'requested',
  CONFIRMED: 'confirmed',
  PENDING: 'pending',
  FAILED: 'failed',
  UNKNOWN: 'unknown',
  CANCELLED: 'cancelled',
});

// A mapped hotel's status in a DigiTrips city availability summary.
// Frozen so the generated declaration carries the string literals rather than
// widening every member to string.
export const DIGITRIPS_HOTEL_STATUS = Object.freeze({
  AVAILABLE: 'available',
  ON_REQUEST: 'onRequest',
  UNAVAILABLE: 'unavailable',
});

/**
 * @param {{ source?: string } | null | undefined} itinerary
 * @returns {boolean}
 */
export const isDigitripsItinerary = itinerary => itinerary?.source === ITINERARY_SOURCE.DIGITRIPS;

/**
 * @param {{ itinerary?: { segments?: { type?: string, countryCode?: string, source?: string }[] } } | null | undefined} itinerary
 * @returns {boolean}
 */
export const isOnlyRestOfWorld = itinerary => {
  const accommodations = reduce(
    itinerary?.itinerary?.segments,
    (acc, segment) => {
      if (segment.type === 'stay' /*SEGMENT_TYPE.STAY*/) {
        const restOfWorld = isDigitripsStay(segment) || (segment.countryCode && isRestOfWorld(segment.countryCode));
        if (!restOfWorld) {
          acc.isOnlyROW = false;
        }
      }
      return acc;
    },
    { isOnlyROW: true },
  );
  return accommodations.isOnlyROW;
};

// Stays are the only segments that place a booking: a connecting flight through Doha does not make
// an Okavango safari Rest of World. A stay with no country code is skipped rather than counted as
// either side, so an itinerary carrying none yields undefined and the caller omits the field.
/**
 * @param {{ itinerary?: { segments?: { type?: string, countryCode?: string }[] } } | null | undefined} itinerary
 * @returns {'Africa & Rest of World' | 'Africa' | 'Rest of World' | undefined}
 */
export const regionTagFor = itinerary => {
  const stays = (itinerary?.itinerary?.segments || []).filter(segment => segment.type === SEGMENT_TYPE.STAY && segment.countryCode);
  const hasRestOfWorld = stays.some(stay => isRestOfWorld(stay.countryCode));
  const hasAfrica = stays.some(stay => !isRestOfWorld(stay.countryCode));

  if (hasAfrica && hasRestOfWorld) {
    return 'Africa & Rest of World';
  }
  if (hasAfrica) {
    return 'Africa';
  }
  if (hasRestOfWorld) {
    return 'Rest of World';
  }
  return undefined;
};
